import React from 'react';
import { InfoIcon, CheckIcon, PackageIcon } from 'lucide-react';
import { useFunnel } from '../../contexts/FunnelContext';
import type { StepErrors } from '../../utils/validation';
import type { Bedrooms, HomeType } from '../../types/funnel';
import type { StringKey } from '../../data/i18n';
import { propertyFor, type PropertyKey } from '../../data/propertyImages';
import { StepHeading } from './StepHeading';
import { BrandPhoto } from './BrandPhoto';
import { PropertyPhoto } from './PropertyPhoto';
import { SuggestedItems } from './SuggestedItems';
const types: {
  value: HomeType;
  key: StringKey;
  property: PropertyKey | null;
}[] = [{
  value: 'apartment',
  key: 'htApartment',
  property: 'apt_mid'
}, {
  value: 'house',
  key: 'htHouse',
  property: 'house_small'
}, {
  value: 'townhome',
  key: 'htTownhome',
  property: 'townhome'
}, {
  value: 'office',
  key: 'htOffice',
  property: null
}];
const beds: Bedrooms[] = ['studio', '1', '2', '3', '4', '5'];
export function bedroomLabel(b: Bedrooms, t: (k: StringKey, v?: Record<string, string | number>) => string): string {
  if (b === 'studio') return t('bedStudio');
  if (b === 'few') return t('bedFew');
  if (b === '5') return t('bed5');
  if (b === '1') return t('bedOne');
  return t('bedMany', {
    n: b
  });
}
export function StepSize({
  errors
}: {
  errors: StepErrors;
}) {
  const {
    t,
    lang,
    data,
    update,
    variant,
    track
  } = useFunnel();
  const selectBeds = (b: Bedrooms) => {
    update({
      bedrooms: b
    });
    track('MoveSizeSelected', {
      bedrooms: b
    });
  };
  return <div data-id="element-926" data-mp="mgqje2k4tj3a">
      <StepHeading title={t('sizeTitle')} data-id="element-927" data-mp="mgi7acvu2ht5" />
      <h2 className="mb-3 text-sm font-semibold text-ink" data-id="element-928" data-mp="res4et14bshe">{t('typeLabel')}</h2>
      <div role="radiogroup" aria-label={t('typeLabel')} className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-id="element-929" data-mp="w61qjjngkdoj">
        {types.map(h => {
        const sel = data.homeType === h.value;
        return <button key={h.value} type="button" role="radio" aria-checked={sel} onClick={() => update({
          homeType: h.value
        })} className={`overflow-hidden rounded-xl border-2 text-left transition-[border-color,transform] duration-150 ease-out active:scale-[0.98] ${sel ? 'border-lime-dark ring-2 ring-lime' : 'border-ink-200 hover:border-ink-400'}`} data-id="element-930" data-mp="3pha9ue9wwc8">
              {h.property ? <PropertyPhoto property={h.property} lang={lang} decorative className="aspect-[4/3] w-full" data-id="element-931" data-mp="i447110csuu" /> : <BrandPhoto role="commercial" alt="" className="aspect-[4/3] w-full" data-id="element-932" data-mp="33x02j3abxaae" />}
              <span className={`block px-3 py-3 font-semibold ${sel ? 'bg-ink text-white' : 'bg-white text-ink'}`} data-id="element-933" data-mp="3iuaeseusqk76">{t(h.key)}</span>
            </button>;
      })}
      </div>
      {errors.homeType && <p className="mt-2 text-sm font-medium text-magenta-dark" data-id="element-934" data-mp="y2qpxikd0qp6">{t(errors.homeType)}</p>}

      {data.homeType === 'office' ? <OfficeSizePicker error={errors.officeSize ? t(errors.officeSize) : undefined} data-id="element-935" data-mp="t3x9pidoullb" /> : <>
      <h2 className="mb-3 mt-10 font-display text-2xl font-black text-magenta" data-id="element-936" data-mp="10mmzzrkt5pur">{t('bedTitle')}</h2>
      {variant.preset.bedrooms && <p className="mb-3 flex items-start gap-2 text-sm text-ink-600" data-id="element-937" data-mp="rnjgwnln5ag9">
          <InfoIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-938" data-mp="rpcrgdzdketj" />
          {t('adNote')}
        </p>}
      <div role="radiogroup" aria-label={t('bedTitle')} className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-id="element-939" data-mp="1jefszlzvnb2h">
        {beds.map(b => {
          const sel = data.bedrooms === b;
          return <button key={b} type="button" role="radio" aria-checked={sel} onClick={() => selectBeds(b)} className={`relative overflow-hidden rounded-xl border-2 text-left transition-[border-color,transform] duration-150 ease-out active:scale-[0.98] ${sel ? 'border-lime-dark ring-2 ring-lime' : 'border-ink-200 hover:border-ink-400'}`} data-id="element-940" data-mp="1amnn7olssq48">
              <PropertyPhoto property={propertyFor(b, data.homeType)} lang={lang} decorative className="aspect-[4/3] w-full" data-id="element-941" data-mp="jgi4sveaynas" />
              <span className={`block whitespace-nowrap px-3 py-2.5 text-sm font-semibold ${sel ? 'bg-ink text-white' : 'bg-white text-ink'}`} data-id="element-942" data-mp="eioxu056dp9g">{bedroomLabel(b, t)}</span>
              {sel && <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-lime text-ink" aria-hidden="true" data-id="element-943" data-mp="5i0jf4bvdmw1">
                  <CheckIcon className="h-4 w-4" strokeWidth={3} data-id="element-944" data-mp="x6tu6mji67y8" />
                </span>}
            </button>;
        })}
      </div>
      <button type="button" role="radio" aria-checked={data.bedrooms === 'few'} onClick={() => selectBeds('few')} className={`mt-3 inline-flex min-h-[48px] items-center gap-2 rounded-xl border-2 px-4 text-sm font-semibold transition-[border-color,background-color] duration-150 ease-out ${data.bedrooms === 'few' ? 'border-ink bg-ink text-white' : 'border-ink-200 bg-white text-ink hover:border-ink-400'}`} data-id="element-945" data-mp="se0qm1zrt5nq">
        <PackageIcon className="h-4 w-4" aria-hidden="true" data-id="element-946" data-mp="33sc5wcgesenc" />
        {t('bedFew')}
      </button>
      <p className="mt-2 text-xs text-ink-500" data-id="element-947" data-mp="37ruj0m1znlfm">{lang === 'es' ? 'Las fotos son ejemplos de referencia; cambian según el tipo de vivienda.' : 'Photos are examples for reference and change with the home type you pick.'}</p>
      {errors.bedrooms && <p className="mt-2 text-sm font-medium text-magenta-dark" data-id="element-948" data-mp="3mqofu0f59sjz">{t(errors.bedrooms)}</p>}

      {data.bedrooms && data.bedrooms !== 'few' && <section className="mt-10 rounded-2xl bg-white p-5 ring-1 ring-ink-200" data-id="element-949" data-mp="3riobhgo6lzar">
          <h2 className="font-display text-lg font-extrabold text-ink" data-id="element-950" data-mp="3vj8akpsbfczj">{t('suggestTitle')}</h2>
          <p className="mb-4 mt-1 text-sm text-ink-500" data-id="element-951" data-mp="4dglzuu6s8eu">{t('suggestBody')}</p>
          <SuggestedItems data-id="element-952" data-mp="9bifl9p8nq4k" />
        </section>}
      </>}
    </div>;
}
const officeCopy = {
  en: {
    title: 'How big is the office?',
    sizes: {
      small: ['Small office', '1–5 workstations'],
      medium: ['Medium office', '6–15 workstations'],
      large: ['Large office', '16+ workstations · reviewed with you']
    },
    note: 'You’ll choose desks, chairs, monitors and files in the item list.'
  },
  es: {
    title: '¿Qué tamaño tiene la oficina?',
    sizes: {
      small: ['Oficina pequeña', '1–5 estaciones'],
      medium: ['Oficina mediana', '6–15 estaciones'],
      large: ['Oficina grande', '16+ estaciones · lo revisamos con usted']
    },
    note: 'Elegirá escritorios, sillas, monitores y archivos en la lista de artículos.'
  }
};
function OfficeSizePicker({
  error
}: {
  error?: string;
}) {
  const {
    lang,
    data,
    update,
    track
  } = useFunnel();
  const c = officeCopy[lang];
  return <section className="mt-10" data-id="element-953" data-mp="13pjh3gz4e3ul">
      <h2 className="mb-3 font-display text-2xl font-black text-magenta" data-id="element-954" data-mp="13qm4hex8ho5r">{c.title}</h2>
      <div role="radiogroup" aria-label={c.title} className="grid gap-3 sm:grid-cols-3" data-id="element-955" data-mp="yqzudecpco16">
        {(['small', 'medium', 'large'] as const).map(s => {
        const sel = data.officeSize === s;
        const [title, body] = c.sizes[s];
        return <button key={s} type="button" role="radio" aria-checked={sel} onClick={() => {
          update({
            officeSize: s
          });
          track('MoveSizeSelected', {
            office: s
          });
        }} className={`relative rounded-xl border-2 bg-white p-4 text-left transition-[border-color] duration-150 ease-out ${sel ? 'border-lime-dark ring-2 ring-lime' : 'border-ink-200 hover:border-ink-400'}`} data-id="element-956" data-mp="3fer050q9vfhx">
              <span className="block font-display text-lg font-extrabold text-ink" data-id="element-957" data-mp="2mo8xu1xdhi5k">{title}</span>
              <span className="mt-1 block text-sm text-ink-500" data-id="element-958" data-mp="2mq243g42q0d1">{body}</span>
              {sel && <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-lime text-ink" aria-hidden="true" data-id="element-959" data-mp="kwvtpf38beak">
                  <CheckIcon className="h-4 w-4" strokeWidth={3} data-id="element-960" data-mp="34rlj73caiyux" />
                </span>}
            </button>;
      })}
      </div>
      <p className="mt-3 text-sm text-ink-500" data-id="element-961" data-mp="2v2tv56acoypa">{c.note}</p>
      {error && <p className="mt-2 text-sm font-medium text-magenta-dark" data-id="element-962" data-mp="2v10mjlg6g4en">{error}</p>}
    </section>;
}